import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { ScrollToTop } from "@/components/landing/ScrollToTop";
import { Nav } from "@/components/landing/Nav";
import { Hero } from "@/components/landing/Hero";
import { Stats } from "@/components/landing/Stats";
import { About } from "@/components/landing/About";
import { Products } from "@/components/landing/Products";
import { UseCases } from "@/components/landing/UseCases";
import { Proof } from "@/components/landing/Proof";
import { Architecture } from "@/components/landing/Architecture";
import { Culture } from "@/components/landing/Culture";
import { Clients } from "@/components/landing/Clients";
import { Contact } from "@/components/landing/Contact";
import { Footer } from "@/components/landing/Footer";
import { CookieBanner } from "@/components/landing/CookieBanner";
import { SEO } from "@/components/SEO";

import PlatformPage from "@/pages/PlatformPage";
import CorePage from "@/pages/CorePage";
import GripPage from "@/pages/GripPage";
import IntelligencePage from "@/pages/IntelligencePage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import MFInsureCoreCaseStudy from "@/pages/MFInsureCoreCaseStudy";
import GripCaseStudy from "@/pages/GripCaseStudy";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import NotFoundPage from "@/pages/NotFoundPage";

const Landing = () => (
  <main data-testid="landing-page" className="bg-white text-ink">
    <SEO
      title="Gradatim — Insurance Operating Platform for Life, Group & Embedded"
      description="Unified insurance operating platform — policy administration, distribution, workflows, analytics and AI-driven operations. Trusted by leading insurers across India and beyond."
      path="/"
    />
    <Nav />
    <Hero />
    <Stats />
    <About />
    <Products />
    <UseCases />
    <Proof />
    <Architecture />
    <Culture />
    <Clients />
    <Contact />
    <Footer />
  </main>
);

// Subtle page transition: short fade + small upward motion.
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -4 }}
    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Landing /></PageTransition>} />
        <Route path="/platform" element={<PageTransition><PlatformPage /></PageTransition>} />
        <Route path="/products/core" element={<PageTransition><CorePage /></PageTransition>} />
        <Route path="/products/grip" element={<PageTransition><GripPage /></PageTransition>} />
        <Route path="/products/intelligence" element={<PageTransition><IntelligencePage /></PageTransition>} />
        <Route path="/insights/case-study" element={<PageTransition><CaseStudyPage /></PageTransition>} />
        <Route path="/insights/mfinsure-core" element={<PageTransition><MFInsureCoreCaseStudy /></PageTransition>} />
        <Route path="/insights/mfinsure-grip" element={<PageTransition><GripCaseStudy /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

export default function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#020617",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 0,
            },
          }}
        />
        <BrowserRouter>
          <ScrollToTop />
          <AnimatedRoutes />
          <CookieBanner />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}
